import type { JSONContent } from '@tiptap/core'

export const CONSENT_VARIABLE_NAMES = ['cgu', 'privacyPolicy'] as const

export type ConsentVariableName = (typeof CONSENT_VARIABLE_NAMES)[number]

export const CONSENT_VARIABLE_NODE_NAME = 'consentVariable'

const WORDING_VARIABLE_PATTERN = /\{(cgu|privacyPolicy)(?::([^}]+))?\}/g

export function parseWordingToDoc(wording: string): JSONContent {
  const text = wording.replace(/\r?\n/g, ' ')
  const content: JSONContent[] = []
  let lastIndex = 0

  for (const match of text.matchAll(WORDING_VARIABLE_PATTERN)) {
    if (match.index > lastIndex) {
      content.push({ type: 'text', text: text.slice(lastIndex, match.index) })
    }
    content.push({
      type: CONSENT_VARIABLE_NODE_NAME,
      attrs: { name: match[1], label: match[2] ?? null }
    })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    content.push({ type: 'text', text: text.slice(lastIndex) })
  }

  return {
    type: 'doc',
    content: [{ type: 'paragraph', ...(content.length ? { content } : {}) }]
  }
}

function serializeNode(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? ''
  if (node.type === CONSENT_VARIABLE_NODE_NAME) {
    const name = String(node.attrs?.name ?? '')
    const label = node.attrs?.label
    return typeof label === 'string' && label.length > 0 ? `{${name}:${label}}` : `{${name}}`
  }
  return (node.content ?? []).map(serializeNode).join('')
}

export function serializeDocToWording(doc: JSONContent): string {
  return (doc.content ?? []).map(serializeNode).join('\n')
}

export function renderWordingWithLabels(wording: string, labels: Record<ConsentVariableName, string>): string {
  return wording.replace(
    WORDING_VARIABLE_PATTERN,
    (_, name: ConsentVariableName, label?: string) => label?.trim() || labels[name]
  )
}
